import React, { FC, useCallback, useEffect, useState } from "react";
import classes from "./MultiInputHeader.module.scss";
import { useGetWorkerInfo } from "../../../hooks/api/workers/useGetWorkerInfo";
import { Switch, TextField, useTheme } from "@mui/material";
import SingleDatePicker from "../../uikit/atoms/datepickers/SingleDatePicker";
//キャンセル
import CancelIcon from "@mui/icons-material/Cancel";
import {
  compareTimeTable,
  getTimesDownTo,
  getTimesUpTo,
  timeList,
} from "../../../utils/variables/timeList";
import SelectBoxForString from "../../uikit/atoms/selectboxes/SelectBoxForString";
import {
  calcTotalTimeByDiary,
  calcTotalTimeByFromAndTo,
} from "../../../utils/functions/calcTotalTime";
import { useRecoilState } from "recoil";
import { userState } from "../../../states/userState";
import { User, Worker } from "../../../types";
import { Dirary } from "../../../types/dirary";
import { setUserToLocalStorage } from "../../../utils/functions/localStorageManager";
import { formatDate } from "../../../utils/functions/formatting";
import { useGetDiaryList } from "../../../hooks/api/posts/useGetDiraryList";
import { Buttons } from "../../uikit/atoms/buttons/Buttons";
import NightShiftToggle from "../../uikit/molecules/NightShiftToggle";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import { isRadioExerciseDiary } from "../../../utils/functions/createDefaultDiary";
import {
  NIGHT_SHIFT_DEFAULT_START,
  NIGHT_SHIFT_THRESHOLD_MIN,
  SHIFT_TYPE_NIGHT,
  SHIFT_TYPE_NORMAL,
} from "../../../utils/variables/shift";

const padTwo = (n: number) => String(n).padStart(2, "0");
const timeToMin = (t: string) => {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
};
//夜勤2直対応: 終了時刻ラベル(§4.2)。24h超え時のみ「（翌 H:mm）」併記。
const formatEndTimeLabel = (endMin: number): string => {
  const base = `${padTwo(Math.floor(endMin / 60))}時${padTwo(endMin % 60)}分`;
  if (endMin > 1440) {
    return `${base}（翌 ${Math.floor((endMin % 1440) / 60)}:${padTwo(
      endMin % 60
    )}）`;
  }
  return base;
};

type Props = {
  setTotalTime: React.Dispatch<
    React.SetStateAction<{ hour: number; min: number; text: string }>
  >;
  totalTime: { hour: number; min: number; text: string };

  workingDate: {
    obj: Date;
    text: string;
  };
  setWorkingDate: React.Dispatch<
    React.SetStateAction<{
      obj: Date;
      text: string;
    }>
  >;
  fromTime: string;
  toTime: string;
  setFromTime: React.Dispatch<React.SetStateAction<string>>;
  setToTime: React.Dispatch<React.SetStateAction<string>>;
  setFetchDiaryList: React.Dispatch<React.SetStateAction<Dirary[]>>;
  diaryList: Dirary[];
  setDiaryList: React.Dispatch<React.SetStateAction<Dirary[]>>;
  shiftType: number; //夜勤2直対応
  setShiftType: React.Dispatch<React.SetStateAction<number>>;
  endMin: number;
};

const MultiInputHeader: FC<Props> = (props) => {
  const {
    totalTime,
    setTotalTime,
    workingDate,
    setWorkingDate,
    fromTime,
    setFromTime,
    setToTime,
    setFetchDiaryList,
    diaryList,
    setDiaryList,
    shiftType,
    setShiftType,
    endMin,
  } = props;
  const [user, setUser] = useRecoilState<User>(userState);
  const theme = useTheme();
  const getWorkerInfo = useGetWorkerInfo();
  const { getDiaryListByWokerAndDay } = useGetDiaryList();
  //社員マスタ情報-4
  // const [workerInfo, setWorkerInfo] = useState({
  //   workerId: "",
  //   workerName: "",
  //   departmentId: null,
  //   departmentName: null,
  //   place: null,
  // });

  //エラーメッセージ-2
  const [errorWorkerName, setErrorWorkerName] = useState({
    isShow: true,
    text: "",
  });

  const [fromTimeList, setFromTimeList] =
    useState<{ id: string; name: string }[]>(timeList);
  const [toTimeList, setToTimeList] =
    useState<{ id: string; name: string }[]>(timeList);

  // const [toTimeList, setToTimeList] = useState<{ id: string; name: string }[]>(
  //   getTimesDownTo(timeList, fromTime)
  // );

  //作業開始日が変更されたとき
  const handleWorkingDateChange = (newDate: any) => {
    setWorkingDate({ obj: newDate, text: formatDate(newDate) });
  };
  //brush-up 2026-06-17改訂: ラジオ体操の自動削除条件。一括は常に真の初回のため、
  //「フォームがラジオ体操唯一の1件」のときだけ削除する(複数明細があるときは消さない)。
  //トグルON・開始時刻↑跨ぎの両経路で共用。
  const isPristineRadioOnly = () =>
    diaryList.length === 1 && isRadioExerciseDiary(diaryList[0]);
  //開始時刻が変更されたとき
  const handleFromTimeChange = (e: string) => {
    //夜勤2直対応: ユーザ操作時のみ閾値跨ぎで自動判定(§4.3)。一括は1つの区分を全作業者へ適用。
    const oldMin = timeToMin(fromTime);
    const newMin = timeToMin(e);
    if (newMin < NIGHT_SHIFT_THRESHOLD_MIN) {
      setShiftType(SHIFT_TYPE_NORMAL);
    } else if (oldMin < NIGHT_SHIFT_THRESHOLD_MIN) {
      //12:00未満→以上に跨いだ → 自動ON(brush-up改訂)。
      //ラジオ削除は「ラジオ唯一の1件」のときだけ。開始時刻はユーザの選択値を尊重(15:00にはしない)。
      setShiftType(SHIFT_TYPE_NIGHT);
      if (isPristineRadioOnly()) {
        setDiaryList((prev) => prev.filter((d) => !isRadioExerciseDiary(d)));
      }
    }
    setFromTime(e);
  };
  //夜勤2直対応(brush-up 2026-06-17改訂): 夜勤トグル操作(一括は常に真の初回)。
  //・ラジオ削除: 「ラジオ唯一の1件」(isPristineRadioOnly)のときだけ。
  //・15:00自動セット: ラジオ削除後にフォームが空(=空 or ラジオ唯一) かつ 開始<12:00 のときだけ。
  //  実明細ありのトグルでは時刻据置・ラジオ据置。OFFは時刻不変。
  const handleShiftToggle = (next: number) => {
    if (next === SHIFT_TYPE_NIGHT) {
      const pristine = isPristineRadioOnly();
      if (pristine) {
        setDiaryList((prev) => prev.filter((d) => !isRadioExerciseDiary(d)));
      }
      const willBeEmpty = pristine || diaryList.length === 0;
      if (willBeEmpty && timeToMin(fromTime) < NIGHT_SHIFT_THRESHOLD_MIN) {
        setFromTime(NIGHT_SHIFT_DEFAULT_START);
      }
    }
    setShiftType(next);
  };
  useEffect(() => {
    //終了時刻強制セット
    // if (compareTimeTable(fromTime, toTime)) setToTime(toTimeList[0].id);
  }, [toTimeList]);

  const checkFromTimeDisabled = useCallback(() => {
    if (diaryList.length > 0 && diaryList[0].isDisabled) {
      return true;
    } else {
      return false;
    }
  }, [diaryList]);

  return (
    <div className={classes.rootContainer}>
      <div className={classes.workerArea}>
        <div className={`${classes.date}`}>
          <SingleDatePicker
            disabled={false}
            currentDate={workingDate.obj}
            label={"作業日"}
            onChange={handleWorkingDateChange}
          />
        </div>
        <div className={classes.time}>
          <SelectBoxForString
            label={"開始時刻"}
            options={fromTimeList}
            required={true}
            select={handleFromTimeChange}
            value={fromTime}
            disabled={checkFromTimeDisabled()}
            width={"75px"}
            height={"32px"}
            fontSize={"16px"}
            setMode={"input"}
            colorMode={"light"}
          />
        </div>
        {/* 夜勤2直対応: 開始時刻セレクトの直横に夜勤トグル(§4.1)。1つで全作業者に適用。 */}
        <NightShiftToggle
          shiftType={shiftType}
          onChange={handleShiftToggle}
          disabled={
            timeToMin(fromTime) < NIGHT_SHIFT_THRESHOLD_MIN &&
            diaryList.length === 0
          }
        />
      </div>
      <div className={classes.dateTimeArea}>
        <div className={classes.firstArea}>
          {/* <div className={classes.date}>
            <SingleDatePicker
              disabled={false}
              currentDate={workingDate}
              label={"作業日"}
              onChange={handleWorkingDateChange}
            />
          </div> */}

          {/* <span className={classes.hyphen}>-</span>
          <div className={classes.time}>
            <SelectBoxForString
              label={"終了時刻"}
              options={toTimeList}
              required={true}
              select={setToTime}
              value={toTime}
              disabled={true}
              width={"85px"}
              height={"32px"}
              fontSize={"16px"}
              setMode={"input"}
              colorMode={"light"}
            />
          </div> */}
          {/* <div className={classes.hasBreak}>
            <p
              style={{
                color: hasBreak ? theme.palette.foreColor.main : "#8d94a2",
              }}
            >
              30分休
            </p>
            <Switch
              checked={hasBreak}
              onChange={() => setHasBreak(!hasBreak)}
              sx={{
                ...(!hasBreak
                  ? { color: "#4a4f58" }
                  : { color: theme.palette.foreColor.main }),
              }}
            />
          </div> */}
          <div className={classes.totalTime}>
            <p>終了時刻</p>
            <div
              style={{
                color:
                  totalTime.hour <= 0
                    ? theme.palette.error.main
                    : theme.palette.foreColor.main,
              }}
            >
              {/* 夜勤2直対応: 24h超え時のみ「（翌 H:mm）」併記(§4.2) */}
              <>{formatEndTimeLabel(endMin)}</>
            </div>
          </div>
          {/* 夜勤2直対応: ON中は藍色チップを常時表示(§4.1/F1-3) */}
          {shiftType === SHIFT_TYPE_NIGHT && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                backgroundColor: theme.palette.foreColor.main,
                color: "#ffffff",
                borderRadius: "6px",
                padding: "4px 10px",
                fontSize: "13px",
                fontWeight: "bold",
                whiteSpace: "nowrap",
                marginLeft: "8px",
              }}
            >
              <DarkModeIcon sx={{ fontSize: 16 }} />
              夜勤入力中
            </div>
          )}
        </div>
      </div>
      <div className={classes.hr} />
    </div>
  );
};

export default MultiInputHeader;
